/**
 * A note from the platform, as the person reading it should see it.
 *
 * The server sends a code and its particulars — nothing-named, queue-full, product-failed — and
 * never a sentence: a class that writes English into a Macedonian conversation is a hardcoded
 * string with extra steps. The sentence is written here, in whichever of the four languages the
 * portal is being read in.
 *
 * A plain string is a note from before this existed, or from somewhere that has not been converted
 * yet; it is shown as it is, which is all it ever was.
 */
/**
 * What the server calls the three things it can print on a picture, and what a person calls them.
 *
 * The server names them in its own words — title, price, headline — because that is what the
 * renderer records. Interpolated straight into a sentence, an owner reading Macedonian was told
 * that "title тешко се чита". The names already have words, in four languages, from the frames on
 * the picture; this is where the one becomes the other.
 */
const ELEMENTS = { title: 'posts.designName', price: 'posts.designPrice', headline: 'posts.designLine' }

function said(t, args) {
  const out = { ...args }

  for (const key of ['element', 'first', 'second']) {
    if (typeof out[key] === 'string' && ELEMENTS[out[key]]) out[key] = t(ELEMENTS[out[key]]).toLocaleLowerCase()
  }

  return out
}

export function noteText(t, note) {
  if (typeof note === 'string') return note
  if (!note?.code) return ''

  return t(`assistant.notes.${note.code}`, { ...said(t, note.args ?? {}), defaultValue: note.code })
}

/**
 * The same thing for a note about a website: why the last read of it found nothing.
 *
 * These arrive as a list, because a read that came back empty usually has more than one reason —
 * no sitemap, then the links that were followed instead, then the browser that could not be
 * started. Shown in the order the engine raised them, which is the order they happened in.
 */
export function siteNoteText(t, note) {
  if (typeof note === 'string') return note
  if (!note?.code) return ''

  return t(`sites.notes.${note.code}`, { ...(note.args ?? {}), defaultValue: note.text ?? note.code })
}

/**
 * Whether a note is a failure or merely a fact. A count of what was done is not red, and a screen
 * that paints every note as an error teaches people to stop reading them.
 */
export function isBadNote(note) {
  const code = typeof note === 'string' ? '' : (note?.code ?? '')

  return !/^(approved-all|rejected-all|changed-|saved-)/.test(code)
}
